import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { PAYMENT_METHOD_LABEL } from "../components/purchasing/PaymentTermsFields";

const CUR_SYM: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
function money(n: number | null, cur: string): string {
  if (n == null) return "—";
  return `${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n)} ${CUR_SYM[cur] ?? cur}`;
}
function fmtDate(d: Date | string | null): string {
  return d ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(d)) : "—";
}
const STATUS_LABEL: Record<string, string> = {
  draft: "Taslak", confirmed: "Onaylı", partially_received: "Kısmi Teslim", received: "Teslim Alındı", cancelled: "İptal",
};

/** Bir satır kalemi için geçmiş alış fiyatı + fark — print (prp-t) tablosu. */
function PoPrintHistory({ itemId, excludePoId, cur }: { itemId: string; excludePoId: string; cur: string }) {
  const q = trpc.purchasing.priceHistory.useQuery({ itemId, excludePoId, limit: 6 }, { staleTime: 10_000 });
  const rows = q.data?.rows ?? [];
  if (rows.length === 0) return <div style={{ fontSize: 10, color: "var(--muted)", padding: "2px 0 8px" }}>Geçmiş alış fiyatı kaydı yok.</div>;
  return (
    <table className="prp-t" style={{ marginBottom: 8 }}>
      <thead><tr><th>Önceki Sipariş</th><th>Tarih</th><th>Tedarikçi</th><th className="n">Birim Fiyat</th><th className="n">Fark</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.poCode}-${i}`}>
            <td><span className="prp-code">{r.poCode}</span></td>
            <td className="n">{fmtDate(r.date)}</td>
            <td>{r.supplierName}</td>
            <td className="n">{money(r.unitPrice, r.currency)}</td>
            <td className="n">{r.delta == null ? "—" : `${r.delta > 0 ? "▲ +" : r.delta < 0 ? "▼ −" : ""}${money(Math.abs(r.delta), r.currency)}${r.deltaPct != null ? ` (${r.delta > 0 ? "+" : "−"}${Math.abs(r.deltaPct).toFixed(1)}%)` : ""}`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PurchaseRequestPrintPage() {
  const [params] = useSearchParams();
  const id = params.get("id") ?? "";
  const q = trpc.purchasing.get.useQuery({ id }, { enabled: !!id });
  const attQ = trpc.purchasing.attachment.list.useQuery({ poId: id }, { enabled: !!id });
  const quotesQ = trpc.purchasing.quote.list.useQuery({ poId: id }, { enabled: !!id });
  const profQ = trpc.settings.companyProfile.get.useQuery(undefined, { staleTime: 5 * 60_000 });
  const p = q.data;
  const quotes = quotesQ.data ?? [];
  const prof = profQ.data;
  const cur = p?.currency ?? "TRY";
  const atts = attQ.data ?? [];

  // Kıyas işaretleri: aynı döviz içinde en düşük toplam + genel en uzun vade (≥2 teklif).
  const bestTotalIds = new Set<string>();
  {
    const groups = new Map<string, Array<{ id: string; total: number }>>();
    for (const q of quotes) if (q.total != null && q.total > 0) { const a = groups.get(q.currency) ?? []; a.push({ id: q.id, total: q.total }); groups.set(q.currency, a); }
    for (const arr of groups.values()) { if (arr.length < 2) continue; const min = Math.min(...arr.map((x) => x.total)); for (const x of arr) if (x.total === min) bestTotalIds.add(x.id); }
  }
  const longestTermIds = new Set<string>();
  {
    const withTerm = quotes.filter((q) => q.termDays != null && q.termDays > 0);
    if (withTerm.length >= 2) { const max = Math.max(...withTerm.map((q) => q.termDays!)); for (const q of withTerm) if (q.termDays === max) longestTermIds.add(q.id); }
  }

  return (
    <div className="prp-root">
      <style>{`
        .prp-root { --ink:#14253E; --line:#d7dce5; --muted:#5b6472; --bg-soft:#f2f5fa; background:#fff; color:#1a2230; }
        .prp-page { max-width: 800px; margin: 0 auto; padding: 20px 24px 40px; font-family: 'Montserrat', system-ui, sans-serif; font-size: 12px; }
        .prp-num { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .prp-head { display:flex; align-items:flex-start; justify-content:space-between; border-bottom: 2px solid var(--ink); padding-bottom: 10px; margin-bottom: 14px; }
        .prp-title { font-size: 20px; font-weight: 800; color: var(--ink); letter-spacing:-0.01em; }
        .prp-sub { font-size: 11px; color: var(--muted); margin-top: 2px; }
        .prp-coblock { display:flex; align-items:center; gap:12px; }
        .prp-co { text-align:right; font-size: 11px; color: var(--muted); }
        .prp-co b { color: var(--ink); font-size: 13px; }
        .prp-logo { max-height: 52px; max-width: 130px; object-fit: contain; }
        .prp-meta { display:grid; grid-template-columns: repeat(3, 1fr); gap: 8px 16px; margin-bottom: 14px; }
        .prp-kv .k { font-size: 9px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color: var(--muted); }
        .prp-kv .v { font-size: 12px; color: var(--ink); font-weight:600; }
        .prp-sec { font-size: 13px; font-weight:700; color: var(--ink); margin: 16px 0 6px; padding-bottom:3px; border-bottom:1px solid var(--line); }
        .prp-pay { display:flex; flex-wrap:wrap; gap:6px 14px; font-size: 11.5px; margin-bottom: 4px; }
        .prp-pay b { color: var(--ink); }
        table.prp-t { width:100%; border-collapse: collapse; font-size: 11px; }
        table.prp-t th { text-align:left; font-size: 9.5px; font-weight:700; letter-spacing:.03em; text-transform:uppercase; color: var(--muted); border-bottom:1.5px solid var(--line); padding:5px 6px; }
        table.prp-t td { padding:4px 6px; border-bottom:1px solid #eef1f6; }
        table.prp-t td.n, table.prp-t th.n { text-align:right; font-family:'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        table.prp-t tfoot td { font-weight:800; border-top:1.5px solid var(--ink); border-bottom:none; color: var(--ink); }
        .prp-code { font-family:'JetBrains Mono', monospace; font-size: 9.5px; color: var(--muted); }
        .prp-hist-lbl { font-size: 10px; font-weight:700; color: var(--muted); text-transform:uppercase; letter-spacing:.03em; margin: 6px 0 3px; }
        .prp-toolbar { position: sticky; top: 0; z-index: 5; background:#fff; display:flex; gap:12px; align-items:center; justify-content:flex-end; padding: 10px 24px; border-bottom:1px solid var(--line); }
        .prp-btn { border:1px solid var(--ink); background: var(--ink); color:#fff; font-weight:600; font-size:13px; padding:7px 16px; border-radius:6px; cursor:pointer; }
        .prp-btn.sec { background:#fff; color: var(--ink); }
        .prp-btn:disabled { opacity:.5; cursor:default; }
        .prp-att { font-size: 11px; color:#1a2230; }
        @media print {
          .prp-toolbar { display:none !important; }
          .prp-page { max-width:none; padding:0; }
          table.prp-t { font-size: 10px; }
          .prp-sec, .prp-head { break-after: avoid; }
          tr { break-inside: avoid; }
        }
        @page { size: A4 portrait; margin: 12mm; }
      `}</style>

      <div className="prp-toolbar">
        <button type="button" className="prp-btn sec" onClick={() => window.close()}>Kapat</button>
        <button type="button" className="prp-btn" onClick={() => window.print()} disabled={!p}>Yazdır</button>
      </div>

      <div className="prp-page">
        {!id ? (
          <div style={{ color: "#b91c1c" }}>Geçersiz bağlantı — PO kimliği yok.</div>
        ) : q.isLoading ? (
          <div style={{ color: "var(--muted)" }}>Talep hazırlanıyor…</div>
        ) : q.isError ? (
          <div style={{ color: "#b91c1c" }}>{q.error?.message}</div>
        ) : p ? (
          <>
            <div className="prp-head">
              <div>
                <div className="prp-title">Satın Alma Talebi</div>
                <div className="prp-sub">No: <span className="prp-num">{p.code}</span> · Durum: {STATUS_LABEL[p.status] ?? p.status} · Para Birimi: {cur}</div>
                <div className="prp-sub">Tedarikçi: <b>{p.supplierName ?? "— atanmadı —"}</b>{p.supplierCode ? ` (${p.supplierCode})` : ""}</div>
              </div>
              <div className="prp-coblock">
                <div className="prp-co">
                  {prof?.name ? <b>{prof.name}</b> : <b>STEPERP</b>}
                  {prof?.address ? <div>{prof.address}</div> : null}
                </div>
                {prof?.logoUrl ? <img className="prp-logo" src={prof.logoUrl} alt={prof.name ?? "Logo"} /> : null}
              </div>
            </div>

            {/* Talep/onay meta */}
            <div className="prp-meta">
              <div className="prp-kv"><div className="k">Talep Eden</div><div className="v">{p.requestedBy ?? "—"}</div></div>
              <div className="prp-kv"><div className="k">Onaylayan</div><div className="v">{p.approvedBy ?? "—"}{p.approvedAt ? ` · ${fmtDate(p.approvedAt)}` : ""}</div></div>
              <div className="prp-kv"><div className="k">Sipariş Tarihi</div><div className="v prp-num">{fmtDate(p.orderDate)}</div></div>
            </div>

            {/* Ödeme vadesi */}
            <div className="prp-sec">Ödeme Vadesi</div>
            <div className="prp-pay">
              <span>Yöntem: <b>{p.paymentMethod ? (PAYMENT_METHOD_LABEL[p.paymentMethod] ?? p.paymentMethod) : "Belirtilmedi"}</b></span>
              {p.paymentMethod === "credit_card" && p.installmentCount ? <span>Taksit: <b>{p.installmentCount}</b></span> : null}
              {p.paymentMethod === "credit_card" && p.banks.length ? <span>Banka(lar): <b>{p.banks.map((b) => b.name).join(", ")}</b></span> : null}
              {p.paymentMethod === "term" && p.termDays != null ? <span>Vade: <b>{p.termDays} gün</b></span> : null}
              {p.paymentNotes ? <span>Not: {p.paymentNotes}</span> : null}
            </div>

            {/* Kalemler */}
            <div className="prp-sec">Sipariş Kalemleri</div>
            <table className="prp-t">
              <thead>
                <tr>
                  <th>Malzeme</th>
                  <th className="n">Miktar</th>
                  <th>Birim</th>
                  <th className="n">Birim Fiyat</th>
                  <th className="n">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {p.lines.map((l) => (
                  <tr key={l.id}>
                    <td><span className="prp-code">{l.itemCode}</span> {l.itemName}</td>
                    <td className="n">{new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(l.qty)}</td>
                    <td>{l.uom}</td>
                    <td className="n">{money(l.unitPrice, cur)}</td>
                    <td className="n">{money(l.lineTotal, cur)}</td>
                  </tr>
                ))}
              </tbody>
              {p.grandTotal != null ? (
                <tfoot><tr><td colSpan={4} style={{ textAlign: "right" }}>GENEL TOPLAM</td><td className="n">{money(p.grandTotal, cur)}</td></tr></tfoot>
              ) : null}
            </table>
            {p.notes ? <div className="prp-sub" style={{ marginTop: 6 }}>Not: {p.notes}</div> : null}

            {/* Alınan teklifler karşılaştırma */}
            {quotes.length > 0 ? (
              <>
                <div className="prp-sec">Alınan Teklifler ({quotes.length})</div>
                <table className="prp-t">
                  <thead><tr><th>Firma</th><th className="n">Miktar</th><th className="n">Birim Fiyat</th><th className="n">Toplam</th><th>Ödeme</th><th>Not</th></tr></thead>
                  <tbody>
                    {quotes.map((qq) => {
                      const won = p.awardedQuoteId === qq.id;
                      const low = bestTotalIds.has(qq.id), long = longestTermIds.has(qq.id);
                      const pay = qq.paymentMethod
                        ? `${PAYMENT_METHOD_LABEL[qq.paymentMethod] ?? qq.paymentMethod}${qq.paymentMethod === "term" && qq.termDays != null ? ` · ${qq.termDays} gün` : ""}${qq.paymentMethod === "credit_card" && qq.installmentCount ? ` · ${qq.installmentCount} taksit` : ""}`
                        : "—";
                      return (
                        <tr key={qq.id} style={won ? { background: "#eafaf0" } : undefined}>
                          <td>{won ? "★ " : ""}{qq.supplierName}{qq.supplierId ? "" : " (kayıtsız)"}</td>
                          <td className="n">{qq.qty != null ? new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(qq.qty) : "—"}</td>
                          <td className="n">{money(qq.unitPrice, qq.currency)}</td>
                          <td className="n">{money(qq.total, qq.currency)}{low ? " ▼" : ""}</td>
                          <td>{pay}{long ? " ⏱" : ""}</td>
                          <td>{qq.note ?? ""}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="prp-sub" style={{ marginTop: 4 }}>{p.awardedQuoteId ? "★ Onaylanan teklif · " : ""}▼ dövizinde en düşük toplam · ⏱ en uzun vade</div>
              </>
            ) : null}

            {/* Geçmiş fiyatlar (kalem başına — yalnız stok kartları; hizmet satırları hariç) */}
            <div className="prp-sec">Geçmiş Alış Fiyatları ve Fark</div>
            {p.lines.filter((l) => l.kind !== "service" && l.itemId).map((l) => (
              <div key={`h-${l.id}`}>
                <div className="prp-hist-lbl">{l.itemCode} · {l.itemName}</div>
                <PoPrintHistory itemId={l.itemId!} excludePoId={p.id} cur={cur} />
              </div>
            ))}

            {/* Alternatif teklif ekleri */}
            {atts.length > 0 ? (
              <>
                <div className="prp-sec">Alternatif Teklif Ekleri ({atts.length})</div>
                <ol className="prp-att">
                  {atts.map((a) => <li key={a.id}>{a.fileName ?? a.url}{a.uploadedBy ? ` — ${a.uploadedBy}` : ""} ({a.kind === "document" ? "PDF" : "Görsel"})</li>)}
                </ol>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
